import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.svg";
import { useLoginMutation, useGetMeQuery } from "../store/api";
import { useDispatch, useSelector } from "react-redux";
import { logout, setAuthUser } from "../store/slices/authSlice";
import { consumeSessionExpiredMessage } from "../utils/authSession";
import { Loader } from "../components/icons";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/300.css";
import "@fontsource/lexend/500.css";
import "@fontsource/lexend/500.css";
import "@fontsource/lexend/600.css";

import portalImage from "../assets/Portal_Login_Page.png";

export default function Login() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const expiredMessage = consumeSessionExpiredMessage();
    const sessionMessage =
      expiredMessage || location.state?.from?.data?.message;

    if (sessionMessage) {
      setError(sessionMessage);
    }
  }, [location.state]);

  const {
    data: meData,
    isLoading: meLoading,
    isSuccess: meSuccess,
    isError: meError,
  } = useGetMeQuery();

  const [login, { isLoading }] = useLoginMutation();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (meSuccess && meData?.data) {
      dispatch(setAuthUser(meData.data));
      navigate("/", { replace: true });
    }
  }, [meData, meSuccess, navigate, dispatch]);

  useEffect(() => {
    if (meError) {
      dispatch(logout());
    }
  }, [meError, dispatch]);

  if (meLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const response = await login({ email, password }).unwrap();
      dispatch(setAuthUser(response?.data));
      navigate("/", { replace: true });
    } catch (err) {
      const errorMessage =
        err?.data?.message || err?.message || "Login failed. Please try again.";
      setError(errorMessage);
    }
  };

  return (
    <div className="flex min-h-screen lg:h-screen flex-col lg:flex-row bg-white lg:overflow-hidden">
      <div className="hidden lg:flex lg:flex-1 flex-col justify-center items-center bg-[#F8F9FA] p-4 lg:p-8 relative overflow-hidden h-full">
        <div className="w-full h-full flex flex-col justify-center items-center">
          <img src={portalImage} alt="Campuses Collage" className="w-full h-full object-contain mix-blend-multiply" />
        </div>
      </div>

      <div className="flex w-full lg:flex-1 flex-col items-center bg-[#fff] px-4 py-8 sm:px-6 lg:px-8 xl:px-12 overflow-y-auto h-full">
        <div className="w-full max-w-[480px] my-auto shrink-0">
          <div className="flex justify-center mb-4 sm:mb-6">
            <img src={logo} alt="Modernage" className="w-[100px] sm:w-[120px]" />
          </div>

          <h2 className="font-['Lexend'] text-[22px] sm:text-[28px] lg:text-[32px] leading-tight font-bold text-center text-[#111111] mb-2">
            Inventory Management
          </h2>
          <p className="text-center font-['Poppins'] text-sm sm:text-[15px] text-[#878686] font-medium leading-6 mb-4 sm:mb-6">
            Organize Your School's Inventory with Ease.
          </p>

          {error && (
            <div className="mb-4 rounded border border-red-400 bg-red-100 p-4 text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="font-['Poppins'] w-full px-2 sm:px-0">
            <h1 className="text-[#333333] font-['Lexend'] font-medium text-[20px] sm:text-[24px] lg:text-[28px] leading-tight mb-4">
              Sign in
            </h1>
            <div className="mb-4">
              <label className="mb-1.5 block text-[#666666] text-sm sm:text-[15px]">
                User name or email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-[#66666659] px-4 py-3 sm:py-3.5 transition focus:border-transparent focus:outline-none focus:ring-1 focus:ring-[#6D4E8A]"
                required
              />
            </div>

            <div className="mb-3">
              <div className="mr-2 flex items-center justify-between">
                <label className="mb-1.5 block text-[#666666] text-sm sm:text-[15px]">
                  Your password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-[#666666CC] text-sm sm:text-[15px] hover:text-gray-700"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-[#66666659] px-4 py-3 sm:py-3.5 transition focus:border-transparent focus:outline-none focus:ring-1 focus:ring-[#6D4E8A]"
                required
              />
            </div>

            <div className="mb-4 sm:mb-6 text-right">
              <Link
                to="/account/forgot-password"
                className="text-[#111111] text-sm sm:text-base hover:text-purple-800 underline"
              >
                Forget your password
              </Link>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-xl bg-[#6D4E8A] px-4 py-3 sm:py-4 text-white font-medium text-base sm:text-[17px] transition hover:bg-[#5f4376] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </button>
            </div>

            <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-red-50 rounded-xl border border-red-100 text-center">
              <p className="text-[11px] sm:text-xs leading-relaxed text-red-600 font-medium font-['Poppins']">
                This portal is for authorized users only. Unauthorized access is strictly prohibited and may be subject to legal action.
              </p>
            </div>
          </form>

          <div className="mt-8 sm:mt-12 text-center pb-4">
            <p className="text-[10px] sm:text-[11px] text-[#878686] font-['Poppins'] leading-relaxed">
              © 2026 Modernage Public School & College. All Rights Reserved.<br className="hidden sm:block" /> Developed by Robotics-World (Pvt.) Ltd
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
